import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import ImageDashboard from "./ImageDashboard";
import ImageUpload from "./ImageUpload";
import { useUserStore } from "context/useUserStore";
import SpecificAPI from "services/SpecificApi";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";
import localization from "services/localization";

export default function ImageManagerModal({
  show,
  onClose,
  onSelectImage,
  loading,
  setLoading,
  selectedImage,
  schoolId,
}) {
  const { appUser, userData } = useUserStore();
  const [isUploading, setIsUploading] = useState(false);
  const [image, setImage] = useState(selectedImage || null);
  const [images, setImages] = useState([]);

  const handleUploadClick = () => setIsUploading(true);
  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

  useEffect(() => {
    if (show) {
      getData();
    }
  }, [show]);

  const getData = async () => {
    try {
      setLoading(true);
      const allResources = await SpecificAPI.getAllResources(
        { enabled: true },
        schoolId,
      );
      const allowedImages = allResources.filter((r) =>
        ["image/jpeg", "image/jpg", "image/png"].includes(r.fileType),
      );
      const imagesWithSignedUrls = await Promise.all(
        allowedImages.map(async (img) => {
          const signed = await SpecificAPI.getResourceURL(img.id, schoolId);
          const apiUrl = `${baseUrl}/api/resources_surl/${schoolId}/${img.id}`;

          return { ...img, signedUrl: signed?.["signed-url"], apiUrl };
        }),
      );

      setImages(imagesWithSignedUrls);
    } catch (error) {
      console.error("Failed to fetch images", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (formData) => {
    try {
      setLoading(true);
      const res = await SpecificAPI.createResources(formData);
      if (res?.status === 200 || res?.status === 201) {
        toast.success("Image uploaded successfully!");
        await getData();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to upload image!");
    } finally {
      setIsUploading(false);
      setLoading(false);
    }
  };

  const handleSelectImage = (url) => {
    setImage(url);
  };

  const handleUseImage = () => {
    onSelectImage(image);
    onClose();
  };

  const handleBackClick = () => setIsUploading(false);

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      dialogClassName="modal-90w"
      fullscreen="md-down"
      className="themed-modal imagemodal"
    >
      <Modal.Body
        style={{ maxHeight: "85vh", overflowY: "auto", position: "relative" }}
      >
        {loading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-50">
            <ClipLoader size={50} color="var(--primary-icon)" />
          </div>
        )}

        {!isUploading ? (
          <ImageDashboard
            images={images}
            selectedImage={image}
            onUploadClick={handleUploadClick}
            onSelectImage={handleSelectImage}
            setImages={setImages}
            loading={loading}
            setLoading={setLoading}
          />
        ) : (
          <ImageUpload
            onResourceUpload={handleImageUpload}
            onBackClick={handleBackClick}
            appUser={appUser}
            userData={userData}
            loading={loading}
            schoolId={schoolId}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          disabled={!image}
          onClick={handleUseImage}
          className="bg-blue-600 disabled:opacity-50 text-white py-2 rounded-lg font-semibold"
        >
          {localization.use_selected_image || "Use Selected Image"}
        </Button>

        <Button variant="secondary" onClick={onClose}>
          {localization.close || "Close"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
